import React, { FC, memo } from 'react';
import styled from 'styled-components';
import { useRouter } from '@blocs.router';
import Button from '@minecraft.button';
import { WindowWidthContext } from '@minecraft.utils';
import { useDrawerToggle } from '@minecraft.drawer';
import { useTranslation } from '@blocs.i18n';
import { ButtonGroup } from '../FilterBasicFormStyles';

const SearchButton = styled(Button)`
  min-width: 6.75rem;
`;

type Props = {
  showFilterSettingsButton?: boolean;
  showAdvancedSearchButton?: boolean;
  showFilterButton?: boolean;
  withFilterDrawer: boolean;
  advancedSearchRoute?: string;
  toggleFilterSettingsModal: () => void;
  toggleFilterBasicOverlay: () => void;
  onNavigateAdvancedSearchRoute?: () => void;
};

const FilterBasicFormButtonGroup: FC<Props> = ({
  showFilterSettingsButton = false,
  showAdvancedSearchButton = false,
  showFilterButton,
  withFilterDrawer,
  advancedSearchRoute,
  toggleFilterSettingsModal,
  toggleFilterBasicOverlay,
  onNavigateAdvancedSearchRoute,
}) => {
  const { t } = useTranslation();
  const { lessSm } = WindowWidthContext.useWidthData();

  const { navigate } = useRouter();

  const { toggleDrawer } = useDrawerToggle();

  const handleGoToAdvancedFiltersClick = (): void => {
    if (onNavigateAdvancedSearchRoute) {
      onNavigateAdvancedSearchRoute();
    }

    navigate({ to: advancedSearchRoute });
  };

  const handleAdvancedFiltersOpen = (): void => {
    if (lessSm) {
      toggleFilterBasicOverlay();
    }

    toggleDrawer();
  };

  const isButtonGroupVisible =
    showFilterSettingsButton ||
    (showAdvancedSearchButton && !!advancedSearchRoute) ||
    withFilterDrawer ||
    showFilterButton;

  return (
    isButtonGroupVisible && (
      <ButtonGroup>
        {showFilterSettingsButton && (
          <Button isSecondary type="button" onClick={toggleFilterSettingsModal}>
            {t('common:filter.settingsButton')}
          </Button>
        )}
        {showAdvancedSearchButton && !!advancedSearchRoute && (
          <Button isSecondary type="button" onClick={handleGoToAdvancedFiltersClick}>
            {t('common:button.goToAdvancedFilters')}
          </Button>
        )}
        {withFilterDrawer && (
          <Button
            isSecondary
            type="button"
            onClick={handleAdvancedFiltersOpen}
            data-qa-id="filter-goToAdvancedFiltersBtn"
          >
            {t('common:button.goToAdvancedFilters')}
          </Button>
        )}
        {showFilterButton && (
          <SearchButton className="cn_mt-3 cn_md:mt-0" type="submit" data-qa-id="filter-submit-button">
            {t('common:button.filter')}
          </SearchButton>
        )}
      </ButtonGroup>
    )
  );
};

export default memo(FilterBasicFormButtonGroup);
